// Map tile provider configurations
// For Mapbox: Set your token in .env as REACT_APP_MAPBOX_TOKEN
// Get token from: https://account.mapbox.com/access-tokens/

export type MapStyle = 'blue' | 'light' | 'dark' | 'streets' | 'satellite' | 'outdoors';

export interface MapTileConfig {
  name: string;
  description: string;
  url: string;
  attribution: string;
  maxZoom: number;
  tileSize?: number;
  zoomOffset?: number;
}

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

export const MAP_STYLES: Record<MapStyle, MapTileConfig> = {
  blue: {
    name: 'Blue',
    description: 'Smooth blue-gray style with excellent readability',
    url: MAPBOX_TOKEN 
      ? `https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
      : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: MAPBOX_TOKEN ? '© Mapbox' : '© CartoDB',
    maxZoom: 19,
    tileSize: MAPBOX_TOKEN ? 512 : 256,
    zoomOffset: MAPBOX_TOKEN ? -1 : 0,
  },
  light: {
    name: 'Light',
    description: 'Clean, minimal style',
    url: MAPBOX_TOKEN 
      ? `https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
      : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: MAPBOX_TOKEN ? '© Mapbox' : '© CartoDB',
    maxZoom: 19,
    tileSize: MAPBOX_TOKEN ? 512 : 256,
    zoomOffset: MAPBOX_TOKEN ? -1 : 0,
  },
  dark: {
    name: 'Dark',
    description: 'Dark theme for reduced eye strain',
    url: MAPBOX_TOKEN
      ? `https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
      : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: MAPBOX_TOKEN ? '© Mapbox' : '© CartoDB',
    maxZoom: 19,
    tileSize: MAPBOX_TOKEN ? 512 : 256,
    zoomOffset: MAPBOX_TOKEN ? -1 : 0,
  },
  streets: {
    name: 'Streets',
    description: 'Detailed street map with full labels',
    url: MAPBOX_TOKEN
      ? `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
      : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: MAPBOX_TOKEN ? '© Mapbox' : '© OpenStreetMap',
    maxZoom: 19,
    tileSize: MAPBOX_TOKEN ? 512 : 256,
    zoomOffset: MAPBOX_TOKEN ? -1 : 0,
  },
  satellite: {
    name: 'Satellite',
    description: 'Satellite imagery with labels',
    url: MAPBOX_TOKEN
      ? `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
      : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: MAPBOX_TOKEN ? '© Mapbox' : '© Esri',
    maxZoom: 19,
    tileSize: MAPBOX_TOKEN ? 512 : 256,
    zoomOffset: MAPBOX_TOKEN ? -1 : 0,
  },
  outdoors: {
    name: 'Outdoors',
    description: 'Topographic style with terrain details',
    url: MAPBOX_TOKEN
      ? `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
      : 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: MAPBOX_TOKEN ? '© Mapbox' : '© OpenTopoMap',
    maxZoom: 19,
    tileSize: MAPBOX_TOKEN ? 512 : 256,
    zoomOffset: MAPBOX_TOKEN ? -1 : 0,
  },
};

export const DEFAULT_MAP_STYLE: MapStyle = 'light';

